// src/components/AdminManagement.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';

const AdminManagement = () => {
  const { adminUser } = useContext(AuthContext);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', role: '', password: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Role definitions with access permissions (aligned with backend model)
  const roles = {
    'Super Admin': ['All Pages'],
    'Doctor Manager': ['Doctors', 'Appointments'],
    'Finance Manager': ['Payments', 'Refunds', 'Financial Reports'],
    'Support Staff': ['Patients', 'Refunds'],
    'Operations Manager': ['Doctors', 'Patients', 'Appointments', 'Refunds'],
  };

  // Define the initial Super Admin identifiers (matches backend seeded data)
  const INITIAL_SUPER_ADMIN_EMAIL = 'Shivendra1235@example.com';
  const INITIAL_SUPER_ADMIN_ID = 'initial-super-admin-id'; // Replace with actual _id from your backend

  useEffect(() => {
    const fetchAdmins = async () => {
      if (!adminUser || !adminUser.accessToken) {
        setSnackbar({ open: true, message: 'Not authenticated. Please log in.', severity: 'error' });
        return;
      }
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        setLoading(true);
        const res = await api.get('/admin/admins', config);
        setAdmins(res.data || []);
      } catch (err) {
        console.error('Admin fetch error:', err.response?.data || err.message);
        setSnackbar({
          open: true,
          message: 'Failed to fetch admins: ' + (err.response?.data?.error || 'Unknown error'),
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchAdmins();
  }, [adminUser]);

  const isSuperAdmin = adminUser?.role === 'Super Admin';

  const validateForm = () => {
    const { name, email, role, password } = formData;
    if (!name || name.length < 2) return 'Name must be at least 2 characters';
    if (!email || !/\S+@\S+\.\S+/.test(email)) return 'Valid email is required';
    if (!role || !Object.keys(roles).includes(role)) return 'Valid role is required';
    if (createDialogOpen && (!password || password.length < 6)) return 'Password must be at least 6 characters';
    return null;
  };

  const handleCreateAdmin = async () => {
    const validationError = validateForm();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }

    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      setLoading(true);
      const res = await api.post('/admin/admins', formData, config);
      setAdmins([...admins, res.data]);
      setCreateDialogOpen(false);
      setFormData({ name: '', email: '', role: '', password: '' });
      setSnackbar({ open: true, message: 'Admin created successfully', severity: 'success' });
    } catch (err) {
      console.error('Create admin error:', err.response?.data || err.message);
      setSnackbar({
        open: true,
        message: 'Failed to create admin: ' + (err.response?.data?.error || 'Unknown error'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = async () => {
    const validationError = validateForm();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }

    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      setLoading(true);
      const payload = { ...formData };
      if (!payload.password) delete payload.password; // omit password if not updated
      const res = await api.put(`/admin/admins/${selectedAdmin._id}`, payload, config);
      setAdmins(admins.map(admin => (admin._id === selectedAdmin._id ? res.data : admin)));
      setEditDialogOpen(false);
      setSelectedAdmin(null);
      setFormData({ name: '', email: '', role: '', password: '' });
      setSnackbar({ open: true, message: 'Admin updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Edit admin error:', err.response?.data || err.message);
      setSnackbar({
        open: true,
        message: 'Failed to update admin: ' + (err.response?.data?.error || 'Unknown error'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      setLoading(true);
      await api.delete(`/admin/admins/${selectedAdmin._id}`, config);
      setAdmins(admins.filter(admin => admin._id !== selectedAdmin._id));
      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
      setSnackbar({ open: true, message: 'Admin removed successfully', severity: 'success' });
    } catch (err) {
      console.error('Delete admin error:', err.response?.data || err.message);
      setSnackbar({
        open: true,
        message: 'Failed to remove admin: ' + (err.response?.data?.error || 'Unknown error'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setFormData({ name: admin.name, email: admin.email, role: admin.role, password: '' });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (admin) => {
    setSelectedAdmin(admin);
    setDeleteDialogOpen(true);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Check if the admin is the initial Super Admin by _id with a null check
  const isInitialSuperAdmin = (admin) => admin && admin._id === INITIAL_SUPER_ADMIN_ID;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Admin Management</Typography>
      <Paper sx={{ p: 2 }}>
        {isSuperAdmin && (
          <Button
            startIcon={<Add />}
            variant="contained"
            color="primary"
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Create New Admin
          </Button>
        )}
        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}
        {!loading && admins.length === 0 && (
          <Typography>No admins found.</Typography>
        )}
        {!loading && admins.length > 0 && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Access</TableCell>
                {isSuperAdmin && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin._id}>
                  <TableCell>{admin.name}</TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>{admin.role}</TableCell>
                  <TableCell>{roles[admin.role]?.join(', ') || 'N/A'}</TableCell>
                  {isSuperAdmin && (
                    <TableCell>
                      <Button
                        startIcon={<Edit />}
                        onClick={() => openEditDialog(admin)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<Delete />}
                        onClick={() => openDeleteDialog(admin)}
                        color="error"
                        disabled={isInitialSuperAdmin(admin)} // Disable delete for initial Super Admin
                      >
                        Remove
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Create Admin Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Create New Admin</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
            required
          />
          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
            type="email"
            required
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              {Object.keys(roles).map((role) => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleCreateAdmin} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Admin</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
            required
          />
          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
            type="email"
            required
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              {Object.keys(roles).map((role) => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="New Password (optional)"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleEditAdmin} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Admin Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedAdmin?.name} ({selectedAdmin?.role})?
          </Typography>
          {isInitialSuperAdmin(selectedAdmin) && (
            <Typography color="error" sx={{ mt: 1 }}>
              The initial Super Admin (ID: {INITIAL_SUPER_ADMIN_ID}) cannot be deleted, only updated.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button
            onClick={handleDeleteAdmin}
            color="error"
            disabled={loading || isInitialSuperAdmin(selectedAdmin)}
          >
            {loading ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminManagement;